<script setup lang="ts">
import { Button } from '@dashhudson/dashing-ui/button';
import { computed, type ComputedRef, type Ref, ref } from 'vue';

import { useReportDetails } from '@/app/dashboards/composables/useReportDetails';
import { REPORTS } from '@/app/dashboards/utils/reports.enum';
import type { LegacyDashboardReportData } from '@/components/VisionAi/widgets/visionWidget.types';

type UseReportDetails = (args: {
  report: ComputedRef<LegacyDashboardReportData>;
  loading: Ref<boolean>;
}) => { metricTitle: ComputedRef<string | null> };

const { widgetData } = defineProps<{
  widgetData: LegacyDashboardReportData;
}>();

const emit = defineEmits<{
  expand: [];
}>();

const report = computed(() => widgetData);
const reportConfig = computed(() => (widgetData.type ? REPORTS[widgetData.type] : undefined));

const { metricTitle } = (useReportDetails as unknown as UseReportDetails)({
  report,
  loading: ref(false),
});
const reportName = computed(() => widgetData?.meta?.custom_name || metricTitle.value || 'Report');

function handleExpand() {
  emit('expand');
}
</script>

<template>
  <div
    class="bg-background-base elevation-sm relative flex w-full max-w-full cursor-pointer flex-col gap-6 overflow-clip rounded-md p-6"
    data-test="NarrowReportPlaceholder"
    @click="handleExpand"
  >
    <p v-tooltip="reportName" class="typo-h4 overflow-hidden text-ellipsis whitespace-nowrap">
      {{ reportName }}
    </p>

    <div
      class="pointer-events-none relative flex h-[180px] w-full items-center justify-center overflow-hidden"
      aria-hidden="true"
      data-test="NarrowReportPlaceholder-Preview"
    >
      <img
        v-if="reportConfig?.previewImage"
        :src="reportConfig.previewImage"
        :alt="reportConfig.previewImageAlt"
        :class="['h-full w-full object-contain', reportConfig.previewImageClass]"
      />
      <div v-else class="flex h-full w-full flex-col gap-2">
        <div
          v-for="band in 5"
          :key="band"
          class="bg-background-utility flex-1 rounded-sm opacity-50"
          data-test="NarrowReportPlaceholder-FallbackBand"
        />
      </div>
    </div>

    <div
      class="bg-background-overlay absolute inset-0 flex items-center justify-center"
      data-test="NarrowReportPlaceholder-Overlay"
    >
      <Button icon-right="expand" size="sm" severity="primary" @click.stop="handleExpand">
        View Full Size
      </Button>
    </div>
  </div>
</template>
